import { useCallback, useEffect, useState } from 'react';
import { 
  TextInput, 
  toast,
  Select,
  DataTable,
} from '@etherisc/ui-kit';
import { NeutralButton } from '../../components/buttons';
import { PageContent } from '../../components/layout/PageContent';
import { AdministrativeUnitViewModal } from '../../components/admin/AdministrativeUnitViewModal';

import { getAdministrativeUnitsByLevel } from '../../services/administrativeUnitService';
import type { AdministrativeUnit, AdministrativeUnitFilters } from '../../types/administrativeUnit';
import type { ActionDefinition } from '../../utils/tableHelpers';
import {
  FilterIcon,
  EyeIcon,
  MapIcon,
} from 'lucide-react';
import { textColumn, dateColumn, numberColumn } from '../../utils/columns';

export function AdministrativeUnitsManagement() {
  
  // State management
  const [administrativeUnits, setAdministrativeUnits] = useState<AdministrativeUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [selectedUnit, setSelectedUnit] = useState<AdministrativeUnit | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  // Filters and search
  const [filters, setFilters] = useState<AdministrativeUnitFilters>({
    page: 1,
    limit: 25,
  });
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const showError = (message: string, description?: string) => toast.error(message, { description });

  // Fetch administrative units
  const fetchAdministrativeUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For now, get all administrative units (TODO: implement proper filtering/pagination)
      const allUnits = await getAdministrativeUnitsByLevel(1); // Get provinces for now
      setAdministrativeUnits(allUnits);
      setPagination({
        page: 1,
        limit: allUnits.length,
        totalCount: allUnits.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false
      });
    } catch (error) {
      console.error('Error fetching administrative units:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch administrative units';
      setError(errorMessage);
      showError('Failed to fetch administrative units', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Initial load
  useEffect(() => {
    fetchAdministrativeUnits();
  }, []); // Only run on mount

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters, search: value || undefined, page: 1 };
      fetchAdministrativeUnits();
      return newFilters;
    });
  }, [fetchAdministrativeUnits]);

  // Handle country filter
  const handleCountryFilter = useCallback((countryCode: string) => {
    setFilters(prevFilters => {
      const newFilters = { ...prevFilters, countryCode: countryCode === 'all' ? undefined : countryCode, page: 1 };
      fetchAdministrativeUnits();
      return newFilters;
    });
  }, [fetchAdministrativeUnits]);

  // Handle admin level filter
  const handleAdminLevelFilter = useCallback((level: string) => {
    setFilters(prevFilters => {
      const adminLevel = level === 'all' ? undefined : parseInt(level);
      const newFilters = { ...prevFilters, adminLevel, page: 1 };
      fetchAdministrativeUnits();
      return newFilters;
    });
  }, [fetchAdministrativeUnits]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const clearedFilters = { page: 1, limit: 25 };
    setFilters(clearedFilters);
    setSearchQuery('');
    fetchAdministrativeUnits();
  }, [fetchAdministrativeUnits]);



  const handleView = useCallback((unit: AdministrativeUnit) => {
    setSelectedUnit(unit);
    setIsViewModalOpen(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setIsViewModalOpen(false);
    setSelectedUnit(null);
  }, []);



  // Table columns
  const columns = [
    textColumn<AdministrativeUnit>('countryCode', { header: 'Country', sortable: true }),
    numberColumn<AdministrativeUnit>('adminLevel', { header: 'Level', sortable: true }),
    textColumn<AdministrativeUnit>('adminName', { header: 'Name', sortable: true }),
    textColumn<AdministrativeUnit>('adminCode', { header: 'Code', sortable: true }),
    dateColumn<AdministrativeUnit>('createdAt', { header: 'Created', sortable: true }),
    
    // Actions column
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      size: 80,
      cell: ({ row }: { row: { original: AdministrativeUnit } }) => {
        const unit = row.original;
        
        // Filter visible actions
        const visibleActions = actionDefinitions.filter(action => !action.hidden?.(unit));
        
        return (
          <div className="flex items-center gap-2">
            {visibleActions.map((action) => (
              <button
                key={action.id}
                onClick={() => action.onClick(unit)}
                disabled={action.disabled?.(unit)}
                className={`p-1 rounded transition-colors ${
                  action.intent === 'danger' 
                    ? action.disabled?.(unit) 
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-red-600 hover:text-red-800'
                    : action.disabled?.(unit)
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-blue-600 hover:text-blue-800'
                }`}
                title={action.label}
              >
                {action.icon}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  // Action definitions
  const actionDefinitions: ActionDefinition<AdministrativeUnit>[] = [
    {
      id: 'view',
      label: 'View Details',
      icon: <EyeIcon size={16} />,
      onClick: handleView,
      intent: 'outline',
    },
  ];

  // Page actions
  const pageActions = null;

  if (error && !loading) {
    return (
      <PageContent title="Administrative Units" description="View administrative units (countries, provinces, districts)">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <NeutralButton onClick={() => fetchAdministrativeUnits()}>
            Try Again
          </NeutralButton>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent 
      title="Administrative Units" 
      description="View administrative units (countries, provinces, districts)"
      actions={pageActions}
    >
      {/* Filters */}
      <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <FilterIcon className="w-5 h-5 mr-2" />
            Filters
          </h3>
          <NeutralButton 
            onClick={handleClearFilters}
            size="sm"
          >
            Clear All
          </NeutralButton>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <TextInput
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by name or code..."

            />
          </div>

          {/* Country Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country
            </label>
            <Select
              value={filters.countryCode || 'all'}
              onValueChange={handleCountryFilter}
              placeholder="All countries"
              options={[
                { label: 'All countries', value: 'all' },
                { label: 'Zimbabwe (ZW)', value: 'ZW' },
                { label: 'United States (US)', value: 'US' },
                // Add more countries as needed
              ]}
            />
          </div>

          {/* Admin Level Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Administrative Level
            </label>
            <Select
              value={filters.adminLevel?.toString() || 'all'}
              onValueChange={handleAdminLevelFilter}
              placeholder="All levels"
              options={[
                { label: 'All levels', value: 'all' },
                { label: 'Level 0 (Country)', value: '0' },
                { label: 'Level 1 (Province/State)', value: '1' },
                { label: 'Level 2 (District/County)', value: '2' },
                { label: 'Level 3 (Ward/City)', value: '3' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <MapIcon className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Units</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.totalCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <FilterIcon className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Countries</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(administrativeUnits.filter(u => u.adminLevel === 0).map(u => u.countryCode)).size}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <MapIcon className="w-8 h-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Current Page</p>
              <p className="text-2xl font-bold text-gray-900">{administrativeUnits.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Administrative Units Table */}
      <DataTable
        data={administrativeUnits}
        columns={columns}
        enableSorting={true}
        enableResizing={true}
        loading={loading}
        manualPagination={true}
        pageCount={pagination.totalPages}
        rowCount={pagination.totalCount}
        onPaginationChange={(paginationState) => {
          const newPage = paginationState.pageIndex + 1; // Convert 0-based to 1-based
          const newPageSize = paginationState.pageSize;
          
          setFilters(prevFilters => {
            const newFilters = { ...prevFilters, page: newPage, limit: newPageSize };
            fetchAdministrativeUnits();
            return newFilters;
          });
        }}
        state={{
          pagination: {
            pageIndex: pagination.page - 1, // Convert 1-based to 0-based
            pageSize: pagination.limit,
          },
        }}
        pagination={{
          showSizeSelector: true,
          showPageInfo: true,
          showNavigation: true,
          enableFastNavigation: true,
          enableJumpToPage: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
      />

      {/* View Modal */}
      <AdministrativeUnitViewModal
        unit={selectedUnit}
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
      />
    </PageContent>
  );
}
